import { action, computed, observable } from "mobx";
import { UUID, DbRecording, RecordingStatusLiterals, isTabInRecording } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { DbRecordingEntryWriter } from "./DbRecordingEntryWriter";
import { RecordingCreated, RecordingDeleted, RecordingUpdated } from "../StateEvents";
import { EvidenceDatabase } from "./EvidenceDbStruct";

export class DbRecordingsTableReader {
	private _recordings: DbRecording[] = [];

	protected addOrUpdatedRecordingLocal = action( (recording: DbRecording) => {
		const originalLocation = this._recordings.findIndex( e => e.recordingUuid === recording.recordingUuid);
		if (originalLocation === -1) { 
			this._recordings.push(recording);
		} else {
			this._recordings.splice(originalLocation, 1, recording);
		}
	});

	protected addOrUpdatedRecordingLocalByUuid = async (recordingUuid: UUID) => {
		const recording = await this.db.get("recordings", recordingUuid);
		if (recording != null) {
			this.addOrUpdatedRecordingLocal(recording);
		}
	}

	protected deleteRecordingLocal = action( (recordingUuid: UUID) => {
		this._recordings = this._recordings.filter( e => e.recordingUuid != recordingUuid );
	});

	protected constructor(protected readonly db: EvidenceDatabase, initialRecordingsLoaded: DbRecording[]) {
		// makeObservable<this, "_recordings">(this, {
		// 	"_recordings": observable,
		// });
		this._recordings = observable.array(initialRecordingsLoaded);
		RecordingCreated.addListener( async ({recordingUuid}) => this.addOrUpdatedRecordingLocalByUuid(recordingUuid) );
		RecordingUpdated.addListener( async ({recordingUuid}) => this.addOrUpdatedRecordingLocalByUuid(recordingUuid) );
		RecordingDeleted.addListener( async ({recordingUuid}) => this.deleteRecordingLocal(recordingUuid) );
	}

	static async create() {
		const db = await requireEvidenceDatabase();
		const recordings = await db.getAll("recordings") ?? [];
		return new DbRecordingsTableReader(db, recordings);
	}

	getRecordingsInProgress = async () => {
		const recordings = await this.db.getAll("recordings") ?? [];
		return recordings.filter( ({status: tabsRecordingInProgress}) => 
			tabsRecordingInProgress ===  RecordingStatusLiterals.allTabs ||
			(Array.isArray(tabsRecordingInProgress) && tabsRecordingInProgress.length > 0)
		);
	}

	getRecordingInProgress = async () => {
		return (await this.getRecordingsInProgress())[0];
	}

	@computed get recordings() {
		return this._recordings;
	}

	@computed get recordingsNewestToOldest() {
		return [...this.recordings.values()].toSorted( (a,b) => b.startTimeIso.localeCompare(a.startTimeIso) );
	}

	@computed get recordingWritersNewestToOldest() {
		return this.recordingsNewestToOldest.map( recording =>
			DbRecordingEntryWriter.fromDbAndRecording(this.db, recording)
		)
	}

	@computed get recordingInProgress() {
		return [...this.recordings.values()].find( ({status: tabsRecordingInProgress}) => 
			tabsRecordingInProgress ===  RecordingStatusLiterals.allTabs ||
			(Array.isArray(tabsRecordingInProgress) && tabsRecordingInProgress.length > 0)
		);
	}

	tabIdToRecordingInProgress = (tabId?: number) => {
		const recording = this.recordingInProgress;
		return tabId != null && recording != null && isTabInRecording({tabId, recording}) ? recording : undefined;
	}

	isTabRecording = (tabId?: number) => {
		return this.tabIdToRecordingInProgress != null;
	}

	tabIdToRecordingInProgressUuid = (tabId?: number) =>
		this.tabIdToRecordingInProgress(tabId)?.recordingUuid;

	static #instancePromise: Promise<DbRecordingsTableReader> | undefined;
	static get instancePromise() {
		return DbRecordingsTableReader.#instancePromise ??= DbRecordingsTableReader.create();
	}

}

